// The customer's signature on a card payment (ask A45, Andrew 2026-10-13; register checklist API-063). Legacy posted
// the drawn signature with the tender id to an outside service (`signature.parsepos.com/capture`, register
// `useSignatureApi`) and nothing read it back. Here the register hands it to the cloud, which keeps it with the payment
// in its object store: private, readable only by that location's staff (Order Lookup), kept 540 days (core
// `SIGNATURE_RETENTION_DAYS`) and never logged: the signature is a secret input, so it reaches no event, command log or
// request fingerprint.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { secret } from '../../define/secret.ts';
import { IsoInstant, LocationId, OrderId, TenderId } from '../../primitives/codecs.ts';

/** As drawn on the register's pad or the customer display: SVG path data in a width x height box (core checks the strokes). */
export const CustomerSignature = z
  .object({
    width: z.int().positive().max(4000),
    height: z.int().positive().max(4000),
    paths: z.array(z.string().min(1).max(20_000)).min(1).max(500),
  })
  .meta({ id: 'CustomerSignature' });

/** The request body cap for a signature: well above any signature drawn by hand. */
export const SIGNATURE_BODY_BYTES = 2 * 1024 * 1024;

export const SignatureInvalid = defineError({
  code: 'payments.signature_invalid', httpStatus: 422, message: 'errors.payments.signature_invalid', retryable: false,
  details: z.object({ problem: z.enum(['empty', 'malformed']) }),
});

/** The object store is not configured on this server (an operator error); the register keeps the signature and retries. */
export const SignatureStoreUnavailable = defineError({
  code: 'payments.signature_store_unavailable', httpStatus: 503, message: 'errors.payments.signature_store_unavailable', retryable: true,
  details: z.object({}),
});

export const SaveSignature = defineCommand({
  name: 'payments.saveSignature',
  summary: "Keep the customer's signature with a card payment (540 days, then deleted).",
  input: z.object({
    locationId: LocationId,
    tenderId: TenderId,
    orderId: OrderId,
    signature: secret(CustomerSignature),
  }),
  output: z.object({ tenderId: TenderId, savedAt: IsoInstant, keepUntil: IsoInstant }),
  // The register sends it after the tip (legacy `andTipAndPostSignature`) over the device's cloud link.
  permission: { session: 'device' },
  risk: 'low',
  availability: 'cloud',
  origin: 'any',
  cloudReason: 'Signatures are kept only in the cloud object store, private to the location, for the card networks\' dispute window.',
  emits: [],
  errors: [SignatureInvalid, SignatureStoreUnavailable],
  dryRun: false,
  maxBodyBytes: SIGNATURE_BODY_BYTES,
  agent: { expose: false },
  legacy: ['POST https://signature.parsepos.com/capture'],
});

export const TenderSignature = defineQuery({
  name: 'payments.signature',
  summary: "A card payment's customer signature as an SVG, or null when none was kept (or its 540 days are over).",
  input: z.object({ locationId: LocationId, tenderId: TenderId }),
  output: z.object({ signature: z.object({ svg: z.string(), savedAt: IsoInstant, keepUntil: IsoInstant }).nullable() }),
  // That location's staff who may open an order's details (Order Lookup), e.g. to answer a chargeback.
  permission: 'order_lookup.view',
  availability: 'cloud',
  cloudReason: 'Signatures are kept only in the cloud object store.',
  scope: 'location',
  errors: [SignatureStoreUnavailable],
  agent: { expose: false },
  legacy: [],
});

export const signatureCommands = [SaveSignature] as const;
export const signatureQueries = [TenderSignature] as const;
